import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, MessageCircle, HelpCircle, Send, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Inline Reply Component for Query Notifications
const QueryReplyBox = ({ notification, onReplySent }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const { toast } = useToast();

  // Fetch comments thread when expanded
  const fetchComments = async () => {
    if (!notification.lead_id) return;
    setLoadingComments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/notifications/lead/${notification.lead_id}/comments`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    if (!expanded) {
      fetchComments();
    }
    setExpanded(!expanded);
  };

  const handleSendReply = async (e) => {
    e.stopPropagation();
    if (!replyText.trim() || !notification.lead_id) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      // Post reply as a comment
      await axios.post(
        `${API}/api/notifications/lead/${notification.lead_id}/comments`,
        {
          message: replyText.trim(),
          tagged_analyst_email: notification.senderEmail,
          tagged_analyst_name: notification.senderName
        },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      toast({
        title: "✅ Réponse envoyée",
        description: `Votre réponse a été envoyée à ${notification.senderName}`,
      });

      setReplyText('');
      fetchComments(); // Refresh comments
      if (onReplySent) onReplySent();

    } catch (error) {
      console.error('Error sending reply:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer la réponse",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('fr-CA', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="mt-2 border-t border-slate-200 pt-2" onClick={(e) => e.stopPropagation()}>
      {/* Expand/Collapse Thread Button */}
      <button
        onClick={handleExpand}
        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 mb-2"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? 'Masquer la conversation' : 'Voir la conversation & répondre'}
      </button>

      {expanded && (
        <>
          {/* Comments Thread */}
          {loadingComments ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          ) : comments.length > 0 ? (
            <div className="max-h-32 overflow-y-auto space-y-2 mb-2 bg-slate-50 rounded p-2">
              {comments.map((comment, idx) => (
                <div key={comment.id || idx} className="text-xs">
                  <div className="flex items-center gap-1 text-slate-500">
                    <span className="font-medium text-slate-700">{comment.author_name}</span>
                    <span>•</span>
                    <span>{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-slate-600 mt-0.5">{comment.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 mb-2">Aucun message précédent</p>
          )}

          {/* Reply Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Votre réponse..."
              className="flex-1 px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply(e);
                }
              }}
              data-testid="notification-reply-input"
            />
            <Button
              size="sm"
              onClick={handleSendReply}
              disabled={sending || !replyText.trim()}
              className="h-7 px-2 bg-purple-600 hover:bg-purple-700"
              data-testid="notification-reply-send"
            >
              {sending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// NotificationCenter now receives notifications and unreadCount as props from AppHeader
// to avoid duplicate API calls
const NotificationCenter = ({ notifications: propNotifications, unreadCount: propUnreadCount, onRefresh, onCountChange }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(propNotifications || []);
  const [unreadCount, setUnreadCount] = useState(propUnreadCount || 0);
  const [isOpen, setIsOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { toast } = useToast();

  // Sync props to state when they change
  React.useEffect(() => {
    if (propNotifications) setNotifications(propNotifications);
    if (propUnreadCount !== undefined) setUnreadCount(propUnreadCount);
  }, [propNotifications, propUnreadCount]);

  const markAllAsRead = async () => {
    setClearing(true);
    
    // Optimistically clear UI
    const prevNotifications = notifications;
    const prevCount = unreadCount;
    
    setUnreadCount(0);
    setNotifications([]);
    if (onCountChange) onCountChange(0);
    
    try {
      await axios.put(`${API}/api/notifications/mark-all-read`);
      
      toast({
        title: "✅ Succès",
        description: "Toutes les notifications ont été supprimées",
      });
      
      if (onRefresh) setTimeout(() => onRefresh(), 500);
      
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      // Revert on error
      setNotifications(prevNotifications);
      setUnreadCount(prevCount);
      if (onCountChange) onCountChange(prevCount);
      
      toast({
        title: "Erreur",
        description: "Impossible de supprimer les notifications",
        variant: "destructive"
      });
    } finally {
      setClearing(false);
    }
  };

  const markAsRead = async (notificationId) => {
    // Optimistically update UI immediately
    const newCount = Math.max(0, unreadCount - 1);
    setUnreadCount(newCount);
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, isRead: true } : n
    ));
    
    // Notify parent of count change immediately (for header badge)
    if (onCountChange) onCountChange(newCount);
    
    try {
      await axios.put(`${API}/api/notifications/${notificationId}/read`);
      
      // Refresh data in background (don't await to keep UI responsive)
      if (onRefresh) {
        setTimeout(() => onRefresh(), 500);
      }
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert on error
      setUnreadCount(unreadCount);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, isRead: false } : n
      ));
      if (onCountChange) onCountChange(unreadCount);
      
      toast({
        title: "Erreur",
        description: "Impossible de marquer la notification comme lue",
        variant: "destructive"
      });
    }
  };

  // Handle click on notification - navigate to relevant page
  const handleNotificationClick = (notification) => {
    // For query notifications, navigate to CRM page
    if (notification.type === 'query' && notification.lead_id) {
      setIsOpen(false);
      navigate('/marketing', { state: { selectedLeadId: notification.lead_id } });
      // Mark as read after navigating
      if (!notification.isRead) {
        markAsRead(notification.id);
      }
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'query':
        return <HelpCircle className="h-5 w-5 text-purple-500" />;
      case 'comment':
        return <MessageCircle className="h-5 w-5 text-blue-500" />;
      case 'floorplan_repayment':
        return <span className="text-2xl">🔔</span>;
      case 'appraisal':
        return <span className="text-2xl">🚗</span>;
      case 'appraisal_priced':
        return <span className="text-2xl">✅</span>;
      default:
        return <span className="text-2xl">📢</span>;
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-white hover:bg-gray-700" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[420px] max-h-[600px] overflow-y-auto">
        <div className="p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Badge variant="secondary">{unreadCount} non lue(s)</Badge>
              )}
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  disabled={clearing}
                  className="h-7 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Tout effacer
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <div className="divide-y">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Bell className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>Aucune notification</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-slate-50 transition-colors ${
                  !notification.isRead ? 'bg-blue-50' : ''
                }`}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div 
                      className={`${notification.type === 'query' ? 'cursor-pointer' : ''}`}
                      onClick={() => notification.type === 'query' && handleNotificationClick(notification)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {getNotificationIcon(notification.type)}
                        <h4 className="font-semibold text-sm">{notification.title}</h4>
                        {notification.type === 'query' && (
                          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">
                            Question
                          </Badge>
                        )}
                        {notification.type === 'comment' && (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">
                            Réponse
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{notification.message}</p>
                      {notification.type === 'query' && notification.senderName && (
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                          <MessageCircle className="h-3 w-3" />
                          <span>De: {notification.senderName}</span>
                          {notification.lead_name && <span>• Lead: {notification.lead_name}</span>}
                        </div>
                      )}
                      {notification.type === 'comment' && notification.senderName && (
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                          <MessageCircle className="h-3 w-3" />
                          <span>De: {notification.senderName}</span>
                          {notification.lead_name && <span>• Lead: {notification.lead_name}</span>}
                        </div>
                      )}
                      {notification.stock && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span>Stock: {notification.stock}</span>
                          {notification.client && <span>• {notification.client}</span>}
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.created_at || notification.createdAt).toLocaleString('fr-CA', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    {/* Inline Reply for Query and Comment Notifications */}
                    {(notification.type === 'query' || notification.type === 'comment') && notification.lead_id && (
                      <QueryReplyBox 
                        notification={notification} 
                        onReplySent={() => {
                          if (!notification.isRead) {
                            markAsRead(notification.id);
                          }
                        }}
                      />
                    )}
                  </div>
                  {!notification.isRead && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      className="h-8 w-8 p-0 flex-shrink-0"
                      title="Marquer comme lu"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default React.memo(NotificationCenter);
